import { PackageEvidence } from '@cadstrom/cadstrom-shared';
import { PdfElementType } from '@cadstrom/sigma-engine-shared';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';

import { EvidenceConfidenceBadge } from './evidence-confidence-badge.js';
import { EvidenceThumbnail } from './evidence-thumbnail.js';
import { EvidenceVoteButtons } from './evidence-vote-buttons.js';
import { useShowInDatasheet } from './show-in-datasheet-context.js';

import type { EvidenceVoteAnswers } from './evidence-vote-buttons.js';
import type { ShowInDatasheetContext } from './show-in-datasheet-context.js';

const ROW_QUESTION = 'Is this where this row comes from?';

const ROW_ANSWERS: EvidenceVoteAnswers = {
	[PackageEvidence.EvidenceVotePolarity.For]: 'Yes, this is where this row comes from',
	[PackageEvidence.EvidenceVotePolarity.Against]: 'No, this is not where this row comes from',
};

/** Props for {@link RowEvidenceCard}. */
export interface RowEvidenceCardProps {
	/** File-store identifier of the datasheet the locator belongs to. */
	fileId: string;
	/** The locator, the selected rows it is linked to, and the votes on those links. */
	entry: PackageEvidence.RowEvidenceLocator;
	/** Whether the viewer may vote; the vote buttons are left out otherwise. */
	canVote: boolean;
	/** Called with the stance the viewer chose; `Withdrawn` when they retract their vote. */
	onVote: (polarity: PackageEvidence.EvidenceVotePolarity) => void;
	/** Blocks voting while the viewer's vote on this locator is being saved. */
	isVoting?: boolean;
	/** Fades the card, for a locator that is rejected but still shown. */
	dimmed?: boolean;
	/**
	 * Whether pressing the card's caption or preview shows the locator in the datasheet at once, leaving its votes, hint
	 * and verdict to themselves; its preview otherwise enlarges on hover and offers to open the datasheet.
	 */
	clickShowsInDatasheet?: boolean;
}

/**
 * Where a locator sits, as the card captions it: "Row 3 · table on page 6" for a table row, counting rows from the top
 * of the table with its header rows included, and otherwise the locator's kind and page.
 *
 * @param entry - The locator and its container.
 * @returns The caption.
 */
function captionOf({ locator, container }: PackageEvidence.RowEvidenceLocator): string {
	const page = locator.pageStart === null ? 'datasheet' : `page ${locator.pageStart}`;
	if (locator.kind === PackageEvidence.EvidenceLocatorKind.Row) {
		const parsed = PackageEvidence.EvidenceAddress.parse(locator.address);
		const table = container?.elementType === PdfElementType.Table ? 'table' : 'element';
		return parsed.kind === PackageEvidence.EvidenceLocatorKind.Row ?
				`Row ${parsed.rowIndex + 1} · ${table} on ${page}`
			:	`Row · ${table} on ${page}`;
	}
	if (locator.kind === PackageEvidence.EvidenceLocatorKind.Element) {
		return `${locator.elementType === PdfElementType.Table ? 'Table' : 'Element'} on ${page}`;
	}
	return locator.pageStart === null ? 'Whole datasheet' : `Page ${locator.pageStart}`;
}

/**
 * One datasheet locator linked as the source of the selected package rows: where it sits, the verdict so far, and the
 * viewer's yes or no on whether it is where the rows come from, on one line, then a preview of it when it lies on a
 * page. The question itself is left to a hint beside the vote buttons. Opening it in the datasheet draws the row's
 * bounding box, or its table's when the row could not be placed, in the datasheet the nearest
 * {@link ShowInDatasheetContext} names;
 * when asked, pressing the card's caption or preview does so.
 */
export function RowEvidenceCard({
	fileId,
	entry,
	canVote,
	onVote,
	isVoting = false,
	dimmed = false,
	clickShowsInDatasheet = false,
}: RowEvidenceCardProps) {
	const openDatasheet = useShowInDatasheet();
	const { locator, container } = entry;
	const { pageStart } = locator;
	const bbox = locator.bbox ?? container?.bbox ?? null;
	const caption = captionOf(entry);
	const myVote = entry.myVote === PackageEvidence.EvidenceVotePolarity.Withdrawn ? null : entry.myVote;

	const handleOpenDatasheet =
		pageStart === null ? undefined : (
			() =>
				openDatasheet({
					fileId,
					initialPageNumber: pageStart,
					initialBoundingBoxes: bbox === null ? undefined : [{ page: pageStart, ...bbox }],
				})
		);

	const showOnClick = clickShowsInDatasheet ? handleOpenDatasheet : undefined;

	return (
		<article
			onClick={
				showOnClick === undefined ? undefined : (
					(event) => {
						const { target, currentTarget } = event;
						// Tooltips are portalled outside the card but still bubble here. The caption, preview and votes
						// are buttons of their own; the hint and the verdict only explain.
						if (
							target instanceof Element &&
							currentTarget.contains(target) &&
							target.closest('button, [role="img"]') === null
						) {
							showOnClick();
						}
					}
				)
			}
			className={clsx(
				'flex flex-col gap-2 rounded-md border border-gray-6 px-3 py-2',
				showOnClick !== undefined && 'cursor-pointer transition-colors hover:border-blue-8',
				dimmed && 'opacity-60',
			)}
		>
			<div className='flex items-center justify-between gap-2'>
				<div className='flex min-w-0 items-center gap-1.5'>
					{showOnClick === undefined ?
						<p className='truncate text-gray-12'>{caption}</p>
					:	<ButtonBase
							onClick={showOnClick}
							className={clsx(
								'min-w-0 justify-start rounded text-left',
								'focus-visible:outline-1 focus-visible:outline-offset-1 focus-visible:outline-blue-9',
							)}
						>
							<span className='truncate text-gray-12'>{caption}</span>
						</ButtonBase>
					}
					<EvidenceConfidenceBadge confidence={entry.confidence} />
				</div>
				{canVote && (
					<div className='flex shrink-0 items-center gap-1'>
						<Tooltip title={ROW_QUESTION}>
							<span role='img' aria-label={ROW_QUESTION} className='inline-flex items-center text-gray-a9'>
								<InfoOutlinedIcon aria-hidden style={{ fontSize: 15 }} />
							</span>
						</Tooltip>
						<EvidenceVoteButtons answers={ROW_ANSWERS} myVote={myVote} disabled={isVoting} onVote={onVote} />
					</div>
				)}
			</div>
			{pageStart !== null && (
				<EvidenceThumbnail
					fileId={fileId}
					locatorAddress={locator.address}
					alt={`Row evidence: ${caption.toLowerCase()}`}
					onOpenDatasheet={showOnClick === undefined ? handleOpenDatasheet : undefined}
					onSelect={showOnClick}
				/>
			)}
		</article>
	);
}
